'use client';

import { Fragment, useEffect, useState } from 'react';
import {
  type ColumnDef,
  type ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  type RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';
import { InViewTrigger } from '@/shared/headless/in-view-trigger';
import { Skeleton } from '@/shared/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { cn } from '@/shared/utils/cn';

interface DataTableProps<TData, TValue> {
  readonly columns: ColumnDef<TData, TValue>[];
  readonly data: TData[];
  readonly enablePagination?: boolean;
  readonly onRowSelectionChange?: (selectedRows: TData[]) => void;
  readonly showVerticalBorder?: boolean;
  readonly renderSubComponent?: (row: TData) => React.ReactNode;
  readonly getRowCanExpand?: (row: TData) => boolean;
  readonly onLoadMore?: () => void;
  readonly isLastPage?: boolean;
  readonly isLoading?: boolean;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  enablePagination = true,
  onRowSelectionChange,
  showVerticalBorder = false,
  renderSubComponent,
  getRowCanExpand,
  onLoadMore,
  isLastPage = false,
  isLoading = false,
}: DataTableProps<TData, TValue>) {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    getExpandedRowModel: getExpandedRowModel(),
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onExpandedChange: setExpanded,
    getSubRows: (row: TData) => (row as { subRows?: TData[] }).subRows,
    getRowCanExpand: getRowCanExpand
      ? (row) => getRowCanExpand(row.original)
      : (row) => !!(row.subRows && row.subRows.length > 0) || !!renderSubComponent,
    state: {
      rowSelection,
      expanded,
    },
  });

  // 선택된 행이 변경될 때 부모 컴포넌트에 알림
  useEffect(() => {
    if (onRowSelectionChange) {
      const selectedRows = table.getSelectedRowModel().rows.map((row) => row.original);
      onRowSelectionChange(selectedRows);
    }
  }, [rowSelection]);

  return (
    <div className="flex-1 min-h-0 flex flex-col rounded-md border overflow-auto bg-white">
      <Table>
        <TableHeader className="bg-gray-100 sticky top-0 z-10">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => {
                return (
                  <TableHead
                    className={cn(
                      showVerticalBorder && index < headerGroup.headers.length - 1 && 'border-r',
                      'px-4'
                    )}
                    key={header.id}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody className="bg-white h-full">
          {isLoading && data.length === 0
            ? // 첫 번째 페이지 로딩 중일 때 스켈레톤 표시
              Array.from({ length: 5 }, (_, i) => `skeleton-row-${i}`).map((rowKey) => (
                <TableRow key={rowKey}>
                  {table.getHeaderGroups()[0]?.headers.map((header, index, headers) => (
                    <TableCell
                      className={cn(
                        showVerticalBorder && index < headers.length - 1 && 'border-r',
                        'px-4'
                      )}
                      key={`${rowKey}-${header.id}`}
                    >
                      <Skeleton className="h-5 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : null}
          {(!isLoading || data.length > 0) &&
            (table.getRowModel().rows?.length ? (
              <>
                {table.getRowModel().rows.map((row, rowIndex) => (
                  <Fragment key={row.id}>
                    <TableRow
                      className={cn(
                        rowIndex === table.getRowModel().rows.length - 1 &&
                          !row.getIsExpanded() &&
                          '!border-b'
                      )}
                      data-state={row.getIsSelected() && 'selected'}
                    >
                      {row.getVisibleCells().map((cell, index) => (
                        <TableCell
                          className={cn(
                            showVerticalBorder &&
                              index < row.getVisibleCells().length - 1 &&
                              'border-r',
                            'px-4'
                          )}
                          key={cell.id}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                    {row.getIsExpanded() && renderSubComponent && (
                      <TableRow
                        className={cn(
                          rowIndex === table.getRowModel().rows.length - 1 && '!border-b'
                        )}
                      >
                        <TableCell colSpan={columns.length}>
                          {renderSubComponent(row.original)}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))}
                {isLastPage && data.length > 0 && (
                  <TableRow>
                    <TableCell
                      className="h-16 text-center text-muted-foreground"
                      colSpan={columns.length}
                    >
                      모든 데이터를 불러왔습니다
                    </TableCell>
                  </TableRow>
                )}
              </>
            ) : (
              <TableRow>
                <TableCell
                  className="h-24 text-center"
                  colSpan={columns.length}
                >
                  데이터가 없습니다.
                </TableCell>
              </TableRow>
            ))}
          {!isLastPage && !isLoading && (
            <TableRow>
              <TableCell colSpan={columns.length}>
                <InViewTrigger onEnter={() => onLoadMore?.()} />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
